import { Box, Button, Menu, MenuItem, Typography, Divider, Stack, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { hardDeleteOrder, reorderCompleteOrder } from '../../../../api/orders';
import { getPreviousOrderInfo } from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import { ConfirmDialogModal } from '../../../../reusable-components/confirm-dialog';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import Iconify from '../../../../reusable-components/iconify';
import DeleteOrderModal from '../DeleteOrderModal';
import DownloadOrderDropdown from './DownloadOrderDropdown';
import ReRunDropdown from './ReRunDropdown';
import OrdersSent from './OrdersSent';
import DropdownAction from '../../../../reusable-components/dropdown-action/DropdownAction';
import DropdownExpandableAction from '../../../../reusable-components/dropdown-action/DropdownExpandableAction';

CompletedOrderMoreMenu.propTypes = {
  data: PropTypes.array,
  setIsAddItemsToBudgetEnabled: PropTypes.func,
  setIsRemoveItemsFromBudgetEnabled: PropTypes.func,
  facilityId: PropTypes.number,
  punchout: PropTypes.bool,
  setIsDeleteItemsEnabled: PropTypes.func,
};
export default function CompletedOrderMoreMenu({
  data,
  setIsAddItemsToBudgetEnabled,
  setIsRemoveItemsFromBudgetEnabled,
  facilityId,
  punchout,
  setIsDeleteItemsEnabled,
}) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { orderById } = useSelector((state) => state.orders || {});
  const { orderNumber, deleteFlag } = orderById[id] || {};

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openConfirmDelForever, setOpenConfirmDelForever] = useState(false);

  const isAdmin = useScopeCheck(['Admin']);
  const hasEditOrderPermission = useScopeCheck(['Edit-Order']);
  const hasViewOrderPermission = useScopeCheck(['View-Order']);

  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isViewByVendor = !!searchParams.get('vendorId') && searchParams.get('vendorId') !== 'all';

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenConfirmDelete = (e) => {
    e.preventDefault();
    e.confirmDialog = true;
    setOpenConfirmDelete(true);
    e.stopPropagation();
  };

  const handleOpenConfirmDelForever = (e) => {
    e.preventDefault();
    e.confirmDialog = true;
    setOpenConfirmDelForever(true);
    e.stopPropagation();
  };

  const handleHardDeleteOrder = async () => {
    setLoading(true);
    await hardDeleteOrder(id)
      .then((res) => {
        if (res?.data) {
          navigate(
            `/dashboard/orders?filter=previous&start=${new Date(
              new Date().setDate(new Date().getDate() - 29)
            ).toISOString()}&end=${new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString()}`
          );
        }
      })
      .catch(() => {
        handleClose();
        enqueueSnackbar('Something went wrong', { variant: 'error' });
      });
    setLoading(false);
  };
  const reorderOrder = async () => {
    try {
      await reorderCompleteOrder(id);
    } catch (error) {
      enqueueSnackbar('Error reordering order', { variant: 'error' });
    }
    navigate(`/dashboard/orders/new-order/cart/${facilityId}`);
  };

  const buyAgainOption = (
    <MenuItem
      disableRipple
      disableGutters
      sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
      disabled={punchout || loading}
      onClick={() => reorderOrder()}
    >
      <DropdownAction
        badgeIcon="mdi:shopping-cart-outline"
        iconColor="secondary.main"
        iconBgColor="#F2E3FC"
        textColor="secondary.main"
      >
        Buy Again
      </DropdownAction>
    </MenuItem>
  );

  const canAddItemsToBudget = !deleteFlag && !loading && data.some((row) => !row.deleted && row.removedFromBudget);
  const canRemoveItemsFromBudget = !deleteFlag && !loading && data.some((row) => !row.removedFromBudget);
  const canDeleteItems = !deleteFlag && !loading && data.some((row) => !row.deleted);
  const canDeleteOrder = !deleteFlag && !loading;
  const canHardDeleteOrder = !loading;

  const panelRowSx = (enabled) => ({
    fontSize: '0.875rem',
    color: enabled ? 'grey.700' : 'grey.400',
    cursor: enabled ? 'pointer' : 'not-allowed',
    borderRadius: 1.5,
    px: 1,
    mx: -1,
    py: 0.5,
    '&:hover': enabled ? { bgcolor: 'grey.100', color: 'text.primary' } : {},
  });

  return (
    <>
      <ScopeGuard scopes={['Edit-Order']} allowAdmin>
        <Button
          sx={{ ml: 0, minWidth: 0, px: 1 }}
          variant="outlined"
          size="small"
          color="secondary"
          onClick={handleClick}
        >
          <Iconify icon="mdi:more-vert" />
        </Button>
      </ScopeGuard>

      <Menu
        id="demo-customized-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          list: { 'aria-labelledby': 'demo-customized-button' },
          paper: {
            sx: {
              width: 290,
              p: 1,
              borderRadius: '5px',
              boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
              '& .MuiMenu-list': {
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              },
            },
          },
        }}
      >
        {punchout ? (
          // Disabled MenuItems don't receive pointer events, so wrap in a span for the tooltip
          <Tooltip
            arrow
            title="This order only has punchout, temporary or vendor-added items, which can't be reordered."
            slotProps={{ tooltip: { sx: { textAlign: 'center' } } }}
          >
            <span>{buyAgainOption}</span>
          </Tooltip>
        ) : (
          buyAgainOption
        )}

        <ScopeGuard scopes={['Edit-Order']} allowAdmin>
          <DropdownExpandableAction icon="mdi:send-variant-outline" panel={<ReRunDropdown />}>
            Resend Order
          </DropdownExpandableAction>
        </ScopeGuard>

        <OrdersSent />

        <Divider sx={{ my: 0.5, mx: 2 }} />
        <DropdownExpandableAction
          icon="mdi:tray-download"
          panel={<DownloadOrderDropdown setAnchorEl={setAnchorEl} />}
          disabled={loading}
        >
          Download
        </DropdownExpandableAction>

        <DropdownExpandableAction
          icon="mdi:currency-usd"
          disabled={loading}
          panel={
            <Stack spacing={0.5} sx={{ pl: 5 }}>
              <Box
                sx={panelRowSx(canAddItemsToBudget)}
                onClick={() => {
                  if (!canAddItemsToBudget) return;
                  setIsAddItemsToBudgetEnabled(true);
                  setAnchorEl(null);
                }}
              >
                Add Items to Budget
              </Box>
              <Box
                sx={panelRowSx(canRemoveItemsFromBudget)}
                onClick={() => {
                  if (!canRemoveItemsFromBudget) return;
                  setIsRemoveItemsFromBudgetEnabled(true);
                  setAnchorEl(null);
                }}
              >
                Remove Items from Budget
              </Box>
            </Stack>
          }
        >
          Update Budget Items
        </DropdownExpandableAction>

        <Divider sx={{ my: 0.5, mx: 2 }} />

        <DropdownExpandableAction
          icon="ic:outline-delete"
          iconColor="error.main"
          iconBgColor="#FDEDED"
          textColor="error.main"
          disabled={loading}
          panel={
            <Stack spacing={0.5} sx={{ pl: 5 }}>
              <Box
                sx={panelRowSx(canDeleteItems)}
                onClick={() => {
                  if (!canDeleteItems) return;
                  setIsDeleteItemsEnabled(true);
                  setIsAddItemsToBudgetEnabled(false);
                  setIsRemoveItemsFromBudgetEnabled(false);
                  setAnchorEl(null);
                }}
              >
                Delete Items
              </Box>
              {!isViewByVendor && (
                <Tooltip
                  arrow
                  title={
                    canDeleteOrder
                      ? 'Removes this order from your order list. The order is still saved and can be restored by support.'
                      : ''
                  }
                  slotProps={{ tooltip: { sx: { textAlign: 'center' } } }}
                >
                  <Box
                    sx={panelRowSx(canDeleteOrder)}
                    onClick={(e) => {
                      if (!canDeleteOrder) return;
                      handleOpenConfirmDelete(e);
                    }}
                  >
                    Delete Order
                  </Box>
                </Tooltip>
              )}
              {!isViewByVendor && (
                <ScopeGuard scopes={['Submit-Order']} allowAdmin>
                  <Tooltip
                    arrow
                    title={
                      canHardDeleteOrder
                        ? "Erases this order completely. This can't be undone, and the order can't be recovered."
                        : ''
                    }
                    slotProps={{ tooltip: { sx: { textAlign: 'center' } } }}
                  >
                    <Box
                      sx={{ ...panelRowSx(canHardDeleteOrder), color: canHardDeleteOrder ? 'error.main' : 'grey.400' }}
                      onClick={(e) => {
                        if (!canHardDeleteOrder) return;
                        handleOpenConfirmDelForever(e);
                      }}
                    >
                      Permanently Delete Order
                    </Box>
                  </Tooltip>
                </ScopeGuard>
              )}
            </Stack>
          }
        >
          Delete
        </DropdownExpandableAction>
      </Menu>

      <DeleteOrderModal
        rowId={id}
        content={`Are you sure you want to delete order ${orderNumber}`}
        action={() => {
          handleClose();
          dispatch(getPreviousOrderInfo(id));
        }}
        setOpenConfirm={setOpenConfirmDelete}
        openConfirm={openConfirmDelete}
      />

      <ConfirmDialogModal
        title="Permanently Delete Order"
        content={
          <Typography>
            <b>Warning: </b>Order {orderNumber} will be gone forever.
          </Typography>
        }
        action={() => handleHardDeleteOrder()}
        color="error"
        actionButton="delete"
        setOpenConfirm={setOpenConfirmDelForever}
        openConfirm={openConfirmDelForever}
      />

      {!isAdmin && !hasEditOrderPermission && hasViewOrderPermission && (
        <>
          <Button variant="outlined" color="secondary" onClick={handleClick} size="small">
            <Iconify icon="mdi:download" sx={{ mr: 1 }} />
            Download
            <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} sx={{ ml: 1 }} />
          </Button>
          <Menu
            id="download-menu-nonadmin"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  width: 269,
                  p: 2,
                  borderRadius: '5px',
                  boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                },
              },
            }}
          >
            <DownloadOrderDropdown setAnchorEl={setAnchorEl} />
          </Menu>
        </>
      )}
    </>
  );
}
